import React, { useState, useEffect, useCallback } from 'react';
import {
  getRandomEmbeddings,
  getSimilarEmbeddings,
  searchDatabase,
  getEmbeddingById,
  checkAuth
} from './services/api';
import LoginForm from './components/LoginForm';
// Re-add UMAP import
import { performUMAP, transformWithUMAP } from './services/umap';
import EmbeddingPlot from './components/EmbeddingPlot';
import JsonViewer from './components/JsonViewer';
import SearchResults from './components/SearchResults';
import './App.css';

// Keep our simple projection as fallback
const simpleProjection = (embeddings) => {
  return embeddings.map(vector => {
    // Just take elements from the vector and combine them in a simple way
    // This is NOT dimensionality reduction, just a way to get 2D coordinates
    const x = vector.slice(0, Math.min(vector.length, 100)).reduce((sum, val, i) => sum + val * (i % 5), 0) / 25;
    const y = vector.slice(0, Math.min(vector.length, 100)).reduce((sum, val, i) => sum + val * ((i+2) % 7), 0) / 25;
    return [x, y];
  });
};

function App() {
  // State variables
  const [embeddingData, setEmbeddingData] = useState([]);
  const [selectedPoint, setSelectedPoint] = useState(null);
  // Re-add UMAP model state
  const [umapModel, setUmapModel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginMessage, setLoginMessage] = useState('');
  
  // Load initial random embeddings
  const loadRandomEmbeddings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Get random embeddings
      const data = await getRandomEmbeddings(50);
      
      // Add validation
      if (!data || !Array.isArray(data) || data.length === 0) {
        throw new Error("No valid embedding data received from API");
      }
      
      console.log(`Received ${data.length} embeddings`);
      
      // Extract just the embedding vectors
      const embeddings = data.map((item, idx) => {
        // Detailed logging for debugging
        console.log(`Embedding #${idx}, id=${item.id}:`);
        console.log(`- Type: ${typeof item.embedding}`);
        
        if (!item.embedding) {
          console.warn(`Embedding #${idx} is null or undefined`);
          return new Array(100).fill(0);
        }
        
        if (!Array.isArray(item.embedding)) {
          console.warn(`Embedding #${idx} is not an array but ${typeof item.embedding}`);
          console.log('Value:', item.embedding);
          return new Array(100).fill(0);
        }
        
        if (item.embedding.length === 0) {
          console.warn(`Embedding #${idx} is an empty array`);
          return new Array(100).fill(0);
        }
        
        // Check for non-number values
        const hasNonNumbers = item.embedding.some(val => typeof val !== 'number' || isNaN(val));
        if (hasNonNumbers) {
          console.warn(`Embedding #${idx} contains non-numeric values`);
          console.log('Sample values:', item.embedding.slice(0, 5));
          return new Array(100).fill(0);
        }
        
        console.log(`- Length: ${item.embedding.length}`);
        console.log(`- Sample: [${item.embedding.slice(0, 3).join(', ')}...]`);
        return item.embedding;
      });
      
      // Check embedding dimensions
      if (embeddings.length > 0) {
        console.log(`Embedding dimensions: ${embeddings[0].length}`);
      }
      
      try {
        // Try UMAP with proper error handling
        console.log("Attempting UMAP dimensionality reduction...");
        const { umap, pca, reducedData } = performUMAP(embeddings);  // remove pca from middle const
        console.log("UMAP successful!");

        // Store the UMAP model and PCA info for future transformations
        setUmapModel({ umap, pca }); 
        
        // Combine original data with reduced coordinates
        const processedData = data.map((item, index) => ({
          ...item,
          coordinates: reducedData[index] || [0, 0] // Fallback if reducedData is invalid
        }));
        
        setEmbeddingData(processedData);
      } catch (umapError) {
        // Fallback to simple projection if UMAP fails
        console.error("UMAP failed, using simple projection instead:", umapError);
        const reducedData = simpleProjection(embeddings);
        
        // Combine original data with reduced coordinates
        const processedData = data.map((item, index) => ({
          ...item,
          coordinates: reducedData[index] || [0, 0]
        }));
        
        setEmbeddingData(processedData);
      }
      
    } catch (err) {
      console.error('Error loading embeddings:', err);
      setError('Failed to load embeddings: ' + err.message);
      
      // Attempt to continue with mock data for visualization testing
      const mockData = Array(10).fill().map((_, i) => ({
        id: i + 1,
        data: { text: `Fallback sample ${i + 1}` },
        embedding: Array(10).fill().map(() => Math.random() - 0.5),
        coordinates: [Math.random() * 10 - 5, Math.random() * 10 - 5]
      }));
      
      setEmbeddingData(mockData);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Check auth and load initial data
  useEffect(() => {
    const init = async () => {
      const ok = await checkAuth();
      setLoggedIn(ok);
      if (ok) {
        setLoginMessage('');
        loadRandomEmbeddings();
      } else {
        setLoginMessage('Session expired. Please log in again.');
      }
    };
    init();
  }, [loadRandomEmbeddings]);
  
  // Handle single click on a point
  const handlePointClick = useCallback((point) => {
    setSelectedPoint(point);
  }, []);

  // Fetch and display embeddings similar to the selected point
  const handleFindSimilarPoints = useCallback(async () => {
    if (!selectedPoint) return;
    setLoading(true);
    setError(null);
    try {
      const similar = await getSimilarEmbeddings(selectedPoint.id, 10);
      const existingIds = new Set(embeddingData.map(d => d.id));
      const newPoints = similar.filter(p => !existingIds.has(p.id));

      if (newPoints.length === 0) {
        return;
      }

      const updated = [...embeddingData, ...newPoints];
      const embeddings = updated.map(p => p.embedding);

      const { umap, pca, reducedData } = performUMAP(embeddings);
      setUmapModel({ umap, pca });

      const processed = updated.map((p, idx) => ({
        ...p,
        coordinates: reducedData[idx] || [0, 0]
      }));

      setEmbeddingData(processed);
    } catch (err) {
      console.error('Error fetching similar embeddings:', err);
      setError('Failed to fetch similar embeddings: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedPoint, embeddingData]);


  const handleSearch = useCallback(async () => {
    if (!searchQuery) return;
    setLoading(true);
    setError(null);
    try {
      const results = await searchDatabase(searchQuery);
      setSearchResults(results);
    } catch (err) {
      setError('Failed to search: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  // When a search result is selected, ensure it appears in the plot and is highlighted
  const handleSearchResultSelect = useCallback(async (item) => {
    try {
      setLoading(true);
      // Check if the point already exists in the plot data
      let point = embeddingData.find(d => d.id === item.id);

      // If not present, fetch embedding/coordinates and add to dataset
      if (!point) {
        let embedding = item.embedding;

        // Fetch full item if embedding missing
        if (!embedding) {
          const fetched = await getEmbeddingById(item.id);
          embedding = fetched.embedding;
          item = { ...fetched };
        }

        // Convert embedding from string if necessary
        if (typeof embedding === 'string') {
          try {
            embedding = JSON.parse(embedding);
          } catch (err) {
            embedding = embedding
              .replace(/^\[/, '')
              .replace(/\]$/, '')
              .split(',')
              .map(v => Number(v.trim()));
          }
        }

        // Create a temporary version of the new point (without coordinates yet)
        const newPoint = { ...item, embedding };
        
        // Collect all embeddings including the new one
        const allPoints = [...embeddingData, newPoint];
        const allEmbeddings = allPoints.map(p => p.embedding);

        try {
          console.log("Retraining UMAP with new point...");
          // Retrain UMAP with all data including the new point
          const { reducedData } = performUMAP(allEmbeddings);
          
          // Update all points with their new coordinates
          const updatedData = allPoints.map((p, index) => ({
            ...p,
            coordinates: reducedData[index] || [0, 0]
          }));
          
          // Update state with all the new data
          setEmbeddingData(updatedData);
          
          // Set the selected point to be the newly added one
          setSelectedPoint(updatedData[updatedData.length - 1]);
        } catch (err) {
          console.error("UMAP training failed:", err);
          // Fallback to simple projection for all points
          const reducedData = simpleProjection(allEmbeddings);
          
          const updatedData = allPoints.map((p, index) => ({
            ...p,
            coordinates: reducedData[index] || [0, 0]
          }));
          
          setEmbeddingData(updatedData);
          setSelectedPoint(updatedData[updatedData.length - 1]);
        }
      } else {
        // If point exists, just select it
        setSelectedPoint(point);
      }
    } catch (err) {
      console.error('Error selecting search result:', err);
      setError('Failed to select search result: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [embeddingData]);

  if (!loggedIn) {
    return (
      <div className="App">
        <LoginForm
          message={loginMessage}
          onSuccess={() => {
            setLoggedIn(true);
            setLoginMessage('');
            loadRandomEmbeddings();
          }}
        />
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>Use Case Explorer</h1>
      </header>
      
      <main className="App-main">
        {loading && <div className="loading">Loading...</div>}
        {error && <div className="error">{error}</div>}
        
        <div className="visualization-container">
          <div className="json-container">
            <JsonViewer data={selectedPoint ? selectedPoint.data : null} />
          </div>

          <div className="plot-container">
            <EmbeddingPlot
              data={embeddingData}
              selectedPoint={selectedPoint}
              onPointSingleClick={handlePointClick}
            />
            <div className="controls">
              <button 
                onClick={loadRandomEmbeddings} 
                disabled={loading}
                style={{ marginRight: '10px' }}
              >
                Load Random Embeddings
              </button>
              <button onClick={handleFindSimilarPoints} disabled={loading}>
                Find Similar Embeddings
              </button>
            </div>
          </div>

          <div className="search-container">
            <div className="search-bar">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
              />
              <button onClick={handleSearch} disabled={loading || !searchQuery}>
                Search
              </button>
            </div>
            <SearchResults results={searchResults} onSelect={handleSearchResultSelect} />
          </div>
        </div>
      </main>
      
      <footer className="App-footer">
        <p>
          Click on a point to view its data. Double-click to find similar embeddings.
        </p>
      </footer>
    </div>
  );
}

export default App;
